let _instance = null; // singleton instance

const NA_TEXT = 'not available'; // default text for parameter not available

/**
 * class for accessing system information
 */
export class OMT_SystemInfo {
  /**
   * return singleton instance
   * @returns {OMT_SystemInfo}
   */
  static getInstance() {
    if (!_instance) _instance = new OMT_SystemInfo();
    return _instance;
  }

  /**
   * constructor
   */
  constructor() {
    // instance of https://github.com/faisalman/ua-parser-js
    // eslint-disable-next-line no-undef
    this._uaParser = new UAParser();

    this._systemParams = {
      gameVersion: G.BuildEnvironment.version,
      osName: this._uaParser.getOS().name || NA_TEXT,
      osVersion: this._uaParser.getOS().version || NA_TEXT,
      browserName: this._uaParser.getBrowser().name || NA_TEXT,
      browserVersion: this._uaParser.getBrowser().version || NA_TEXT,
      deviceModel: this._uaParser.getDevice().model || NA_TEXT,
      deviceVendor: this._uaParser.getDevice().vendor || NA_TEXT,
      browserEngineName: this._uaParser.getEngine().name || NA_TEXT,
      browserEngineVersion: this._uaParser.getEngine().version || NA_TEXT,
      isCookOMT: G.BuildEnvironment.isCookOMT ? 1 : 0,
    };
  }

  /**
   * get a reference to the system params object
   */
  get systemParams() {
    return this._systemParams;
  }

  /**
   * get the game version
   * @returns {string}
   */
  get gameVersion() {
    return this._systemParams.gameVersion;
  }

  /**
   * get the operating system name
   * @returns {string}
   */
  get osName() {
    return this._systemParams.osName;
  }

  /**
   * get the operating system version
   * @returns {string}
   */
  get osVersion() {
    return this._systemParams.osVersion;
  }

  /**
   * get the browser name
   * @returns {string}
   */
  get browserName() {
    return this._systemParams.browserName;
  }

  /**
   * get the browser version
   * @returns {string}
   */
  get browserVersion() {
    return this._systemParams.browserVersion;
  }

  /**
   * get the device model
   * @returns {string}
   */
  get deviceModel() {
    return this._systemParams.deviceModel;
  }

  /**
   * get the device vendor / manufacturer
   * @returns {string}
   */
  get deviceVendor() {
    return this._systemParams.deviceVendor;
  }

  /**
   * true if the FBInstant platform is 'WEB'
   * @returns {boolean}
   */
  get deviceIsDesktop() {
    return /*(FBInstant && FBInstant.getPlatform() === 'WEB') ||*/ !(this.deviceIsAndroid && this.deviceIsIOS);
  }

  /**
   * true if the device is iOS
   * @returns {boolean}
   */
  get deviceIsIOS() {
    // UA parser doesn't seem to have iPadOS detection atm, reverted to checking FBInstant
    //return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    //return game.device.iOS || game.device.iPad//FBInstant && FBInstant.getPlatform() === 'IOS';
    const { osName } = this._systemParams;
    const isIOS = osName.toLowerCase() === 'ios';
    return isIOS;
  }

  /**
   * true if the device is Android
   * @returns {boolean}
   */
  get deviceIsAndroid() {
    const { osName } = this._systemParams;
    const isAndroid = osName.toLowerCase() === 'android';
    return isAndroid;
  }

  /**
   * true if the device is Android 5.x or lower
   * @returns {boolean}
   */
  get deviceIsAndroid5() {
    const { osName, osVersion } = this._systemParams;
    const isAndroid5 = osName.toLowerCase() === 'android' && parseInt(osVersion.split('.')[0]) <= 5;
    return isAndroid5;
  }

  /**
   * true if the device is Android 6.x
   * @returns {boolean}
   */
  get deviceIsAndroid6() {
    const { osName, osVersion } = this._systemParams;
    const isAndroid6 = osName.toLowerCase() === 'android' && parseInt(osVersion.split('.')[0]) === 6;
    return isAndroid6;
  }

  /**
   * true if browser is IE 11 or lower
   * @returns {boolean}
   */
  get browserIsIE() {
    const { browserName } = this._systemParams;
    return browserName.toUpperCase() === 'IE';
  }

  /**
   * check for incompatible platforms
   * @returns {boolean}
   */
  get isCompatiblePlatform() {
    return !this.browserIsIE;
  }
}

// this will get overriden later by OMT_Init. This was done for earlier access
if (!window.OMT) window.OMT = {};
OMT.systemInfo = OMT_SystemInfo.getInstance();
